import React from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '10px',
    marginTop: 0,
    marginBottom: 0,
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  listItem: {
    paddingLeft: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  overview: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(4),
  },
  paragraph: {
    marginBottom: theme.spacing(2),
    lineHeight: 1.6,
  },
  subSection: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  subSectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
}));

const TermsOfService = ({ hideHeading = false }) => {
  const classes = useStyles();

  return (
    <Container maxWidth="md">
      <Paper className={classes.root}>
        {!hideHeading && (
          <>
            <Typography variant="h4" gutterBottom align="center">
              Terms & Conditions
            </Typography>
            <Typography variant="subtitle1" gutterBottom align="center">
              Effective Date: April 17, 2025
            </Typography>
            <Typography variant="subtitle2" gutterBottom align="center">
              Applies To: NimblePark mobile application and web platform
            </Typography>
          </>
        )}

        <Box className={classes.section}>
          <Typography variant="body1" className={classes.paragraph}>
            Welcome! By using the NimblePark mobile application ("Mobile App") or web platform, you agree to the following Terms & Conditions (the "Terms" or "Agreement"). For clarity, "NimblePark", "we", or "us" refers to Parinda Intellectual Insights LLP, and "you" refers to the user of our services. These Terms also incorporate our Privacy Policy and any other policies or documents referenced within.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            ACCEPTANCE OF TERMS
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            These Terms govern your access to and use of all features, content, materials, and tools available through the NimblePark platform, including but not limited to parking listings, valet services, operator dashboards, and APIs provided for developers ("Materials"). By using our services, you signify that you have read, understood, and agreed to these Terms. If you do not agree, please refrain from using the app or the Materials. We may revise these Terms at any time. Your continued use after such changes means you accept the revised Terms.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            1. GENERAL USE AND TRADEMARK NOTICE
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            All logos, product names, and services associated with NimblePark are the trademarks or intellectual property of Parinda Intellectual Insights LLP. All third-party marks belong to their respective owners. You may not use any branding from NimblePark or others without express written permission.
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            NimblePark's core function is to provide information regarding the availability and location of parking spaces and valet services. The final decision to use any facility remains with you, and any related risks are solely yours. We do not allocate parking spots nor intervene in the operator's or valet's execution of services.
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            NimblePark does not:
          </Typography>
          <List>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Guarantee safety of property or persons at parking locations" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Support mobile phone usage while driving" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Ensure the availability of a particular space at any given time" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Accept liability for missed parking or any related delays" />
            </ListItem>
          </List>
          <Typography variant="body1" className={classes.paragraph}>
            Our platform is designed for individual, non-commercial use, except where explicitly permitted for operators or developers. Automated scraping or use of bots is strictly prohibited unless authorized by NimblePark.
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            Users must be of legal age and capacity to enter into binding obligations, and agree to keep their information accurate and updated.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            2. PRIVACY & SECURITY
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            By using NimblePark, you acknowledge and accept our Privacy Policy, which forms a part of these Terms. You authorize the collection, use, and sharing of your personal data as described therein.
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            Although we take security seriously, NimblePark is not liable for any damages caused by technical failures, unauthorized access, or security breaches beyond our control.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            3. FEEDBACK & SUBMISSIONS
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            Any feedback, suggestions, or content you submit to NimblePark (collectively, "Feedback") may be used by us in any form or channel, including digital, print, and broadcast, without compensation. Feedback becomes the property of NimblePark and may be published along with your name, initial, and city, unless otherwise requested.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            4. EXTERNAL LINKS
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            NimblePark may include links to external websites or resources. These are provided for convenience only. We do not control or endorse the content or practices of such third-party services, including their data privacy policies. Concerns should be addressed directly with the third-party provider.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            5. USER ACCOUNTS
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            Registration on NimblePark requires a valid mobile number. You consent to receive updates via SMS, email, or phone regarding service changes or updates.
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            You agree:
          </Typography>
          <List>
            <ListItem className={classes.listItem}>
              <ListItemText primary="To use only one account per person" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Not to share your login credentials" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="That you're responsible for any activity through your account" />
            </ListItem>
          </List>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            6. INTELLECTUAL PROPERTY
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            All content on the NimblePark app, including but not limited to graphics, data, APIs, and UI elements ("App Content") is the property of NimblePark or its licensors and is protected by applicable IP laws. You may not reproduce, distribute, or create derivative works based on this content without prior written permission.
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            All rights not expressly granted are reserved.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            7. SERVICE AVAILABILITY
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            We strive to provide seamless access to the app, but NimblePark does not guarantee uninterrupted uptime or availability. Maintenance may be scheduled or unscheduled without prior notice.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            8. TERMINATION OF ACCESS
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            We reserve the right to suspend or terminate your access to the platform at any time, with or without notice, for any reason we deem appropriate.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            9. DISCLAIMER & LIMITATION OF LIABILITY
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            The NimblePark app and all associated services are provided "as is" without warranties of any kind. We disclaim all warranties, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            We are not liable for:
          </Typography>
          <List>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Lost data, revenue, or profits" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Downtime or inability to use the platform" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Damage to property or person through service use" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Any indirect or consequential damages" />
            </ListItem>
          </List>
          <Typography variant="body1" className={classes.paragraph}>
            Some jurisdictions may not allow exclusion of warranties or limitations, so portions of this clause may not apply to you.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            10. COPYRIGHT CLAIMS
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            If you believe your copyrighted material appears on NimblePark without authorization, please notify us with the following:
          </Typography>
          <List>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Your signature" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Description of the copyrighted work" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Location of the infringing material" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Your contact information" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="A good faith statement of unauthorized use" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="A declaration of accuracy under penalty of perjury" />
            </ListItem>
          </List>
          <Typography variant="body1" className={classes.paragraph}>
            Email: legal@nimblepark.in
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            11. GOVERNING LAW
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            These Terms are governed by the laws of Ahmedabad, Gujarat, India. Any disputes shall be resolved exclusively in the courts located within Ahmedabad. You consent to the jurisdiction and waive any objections regarding venue.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            12. APP CHANGES
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            We may update or alter features, services, or these Terms at any time. Continued use of the platform after such changes constitutes acceptance.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            13. CONTENT GUIDELINES & LEGAL COMPLIANCE
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            You agree not to upload, post, or share any material that:
          </Typography>
          <List>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Belongs to someone else without permission" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Is offensive, obscene, misleading, or unlawful" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Threatens national integrity or promotes hate" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Infringes intellectual property" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Impersonates another person" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Contains false or manipulated information" />
            </ListItem>
          </List>
          <Typography variant="body1" className={classes.paragraph}>
            Violations may lead to suspension or legal action under applicable laws.
          </Typography>
        </Box>

        <Divider className={classes.divider} />
        
        <Typography variant="body2" color="textSecondary" align="center">
          Last updated: April 17, 2025
        </Typography>
      </Paper>
    </Container>
  );
};

export default TermsOfService; 